import { useEffect } from 'react'
import { CTA, trackEvent } from '../lib/analytics'

/**
 * The GA4 event for a clicked link, or null when it is not one we measure:
 * a way to contact the company (phone, e-mail, WhatsApp, the map), a quote
 * call-to-action (any link to the #contact form) or the language switch.
 * Parameters name the kind of link only — never a number or an address.
 *
 * @param {HTMLAnchorElement} anchor
 * @returns {[string, Record<string, string>] | null}
 */
function eventFor(anchor) {
  const href = anchor.getAttribute('href') ?? ''
  if (href.startsWith('tel:')) return ['contact_click', { method: 'phone' }]
  if (href.startsWith('mailto:')) return ['contact_click', { method: 'email' }]
  if (/(?:wa\.me|whatsapp\.com)/i.test(href)) return ['contact_click', { method: 'whatsapp' }]
  if (/(?:google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(href)) return ['contact_click', { method: 'map' }]
  if (href.endsWith('#contact')) return ['cta_click', { cta: CTA.getQuote }]
  if (anchor.hasAttribute('hreflang') && anchor.closest('.lang-switcher')) {
    return ['language_switch', { language: anchor.getAttribute('hreflang') }]
  }
  return null
}

/**
 * Measures the site's key interactions with one delegated click listener
 * (mounted once in Router): contact links, quote CTAs and the language
 * switch. Consent-gated like every event (src/lib/analytics.js).
 *
 * @returns {null}
 */
export default function InteractionTracker() {
  useEffect(() => {
    const onClick = (event) => {
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null
      if (!anchor) return
      const tracked = eventFor(anchor)
      if (!tracked) return
      const [name, params] = tracked
      trackEvent(name, { ...params, page_path: window.location.pathname })
    }
    document.addEventListener('click', onClick, { capture: true })
    return () => document.removeEventListener('click', onClick, { capture: true })
  }, [])

  return null
}
